import React, { useEffect, useMemo, useState } from 'react';
import styles from '../intelligenceInsights.module.css';

function formatDuration(totalSeconds) {
    const seconds = Math.max(0, Number(totalSeconds) || 0);
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    if (minutes <= 0) return `${remainder}s`;
    return `${minutes}m ${String(remainder).padStart(2, '0')}s`;
}

function stepIcon(status) {
    if (status === 'complete') return '✓';
    if (status === 'failed') return '✕';
    if (status === 'stuck') return '⚠';
    if (status === 'running') return '◌';
    return '○';
}

function stepStatusClass(status, isCurrent) {
    if (status === 'complete') return styles.insightsGeneratingStageDone;
    if (status === 'failed') return styles.insightsGeneratingStageFailed;
    if (status === 'stuck') return styles.insightsGeneratingStageStuck;
    if (status === 'running' || isCurrent) return styles.insightsGeneratingStageActive;
    return '';
}

const PIPELINE_KEYS = new Set([
    'retrieving_evidence',
    'canonical_synthesis',
    'pricing_treatments',
    'generating_modules',
    'modules',
    'writing_canonical',
]);

const isScopeStepKey = key => typeof key === 'string' && key.startsWith('canonical_scope_');
const isModuleStepKey = key => key && !PIPELINE_KEYS.has(key) && !isScopeStepKey(key);

function aggregateStepStatus(memberSteps) {
    if (!memberSteps.length) return 'pending';
    if (memberSteps.some(step => step.status === 'failed')) return 'failed';
    if (memberSteps.some(step => step.status === 'stuck')) return 'stuck';
    if (memberSteps.some(step => step.status === 'running')) return 'running';
    if (memberSteps.every(step => step.status === 'complete')) return 'complete';
    if (memberSteps.some(step => step.status === 'complete')) return 'running';
    return 'pending';
}

function aggregateElapsedSeconds(memberSteps, status) {
    if (status !== 'running' && status !== 'stuck') return null;
    return Math.max(...memberSteps.map(step => Number(step.elapsed_seconds) || 0));
}

function aggregateDurationSeconds(memberSteps, status) {
    if (status !== 'complete') return null;
    const durations = memberSteps
        .map(step => step.duration_seconds)
        .filter(value => value != null);
    if (!durations.length) return null;
    return Math.max(...durations.map(value => Number(value) || 0));
}

/** Collapse scope passes and module rows into a short, useful checklist. */
function buildDisplaySteps(rawSteps) {
    if (!rawSteps.length) return [];

    const scopeSteps = rawSteps.filter(step => isScopeStepKey(step.key));
    const moduleSteps = rawSteps.filter(step => isModuleStepKey(step.key));
    const byKey = new Map(rawSteps.map(step => [step.key, step]));

    const phases = [
        {
            key: 'retrieving_evidence',
            label: 'Retrieving project evidence',
            members: ['retrieving_evidence'],
        },
        {
            key: 'assessing_scopes',
            label: 'Assessing project scopes',
            members: scopeSteps.map(step => step.key),
        },
        {
            key: 'canonical_synthesis',
            label: 'Synthesising findings, risks and remediations',
            members: ['canonical_synthesis'],
        },
        {
            key: 'pricing_treatments',
            label: 'Pricing remediations',
            members: ['pricing_treatments'],
        },
        {
            key: 'building_modules',
            label: 'Building insight modules',
            members: [...moduleSteps.map(step => step.key), 'generating_modules', 'modules'],
        },
    ];

    let currentAssigned = false;

    return phases
        .map(phase => {
            const memberSteps = phase.members
                .map(key => byKey.get(key))
                .filter(Boolean);
            if (!memberSteps.length && phase.key !== 'retrieving_evidence') {
                return null;
            }
            const status = aggregateStepStatus(memberSteps);
            const completeCount = memberSteps.filter(step => step.status === 'complete').length;
            const totalCount = memberSteps.length;
            let label = phase.label;
            if (phase.key === 'assessing_scopes' && totalCount > 1 && status === 'running') {
                label = `Assessing project scopes (${completeCount}/${totalCount})`;
            }
            if (phase.key === 'building_modules' && totalCount > 1) {
                if (status === 'running' || status === 'complete') {
                    label = `Building insight modules (${completeCount}/${totalCount})`;
                }
            }
            const isCurrent = !currentAssigned && (status === 'running' || status === 'stuck');
            if (isCurrent) currentAssigned = true;
            const failedMember = memberSteps.find(step => step.status === 'failed');
            return {
                key: phase.key,
                label,
                status,
                elapsed_seconds: aggregateElapsedSeconds(memberSteps, status),
                duration_seconds: aggregateDurationSeconds(memberSteps, status),
                error: failedMember?.error || null,
                is_current: isCurrent,
            };
        })
        .filter(Boolean);
}

/**
 * Side dock while a new analysis version generates. Does not block or blur
 * the previous version — user can read existing modules while waiting.
 */
const InsightsGeneratingOverlay = ({ active = false, progress = null }) => {
    const [now, setNow] = useState(() => Date.now());
    const [minimized, setMinimized] = useState(false);

    useEffect(() => {
        if (!active) return undefined;
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, [active]);

    useEffect(() => {
        if (active) return;
        setMinimized(false);
    }, [active]);

    const steps = useMemo(() => {
        const raw = Array.isArray(progress?.steps) ? progress.steps : [];
        if (raw.length) {
            return buildDisplaySteps(raw);
        }
        const legacyStages = Array.isArray(progress?.stages) ? progress.stages : [];
        const legacyModules = Array.isArray(progress?.module_progress)
            ? progress.module_progress
            : [];
        const legacyList = [
            ...legacyStages.map(stage => ({
                key: stage.key,
                label: stage.label,
                status: stage.status === 'active' ? 'running' : stage.status,
                elapsed_seconds: stage.status === 'active' ? progress?.stage_seconds : null,
                duration_seconds: null,
                error: null,
                is_current: stage.status === 'active',
            })),
            ...legacyModules.map(mod => ({
                key: mod.module_key,
                label: mod.title,
                status:
                    mod.status === 'complete' || mod.status === 'complete_with_warnings'
                        ? 'complete'
                        : mod.status === 'failed'
                          ? 'failed'
                          : 'pending',
                elapsed_seconds: null,
                duration_seconds: null,
                error: null,
                is_current: false,
            })),
        ];
        return buildDisplaySteps(legacyList);
    }, [progress]);

    if (!active) return null;

    const startedMs = progress?.started_at ? Date.parse(progress.started_at) : NaN;
    const elapsedSeconds = Number.isFinite(startedMs)
        ? Math.max(0, Math.floor((now - startedMs) / 1000))
        : Number(progress?.elapsed_seconds) || 0;

    const currentStep =
        steps.find(step => step.is_current) ||
        steps.find(step => step.status === 'running' || step.status === 'stuck') ||
        null;

    const failedStep =
        progress?.failed_step ||
        steps.find(step => step.status === 'failed') ||
        null;

    const versionLabel = progress?.analysis_version
        ? `Version ${progress.analysis_version}`
        : 'New version';

    const currentLabel =
        currentStep?.label || progress?.current_stage_label || 'Generating analysis';

    const currentElapsed = currentStep?.elapsed_seconds ?? progress?.stage_seconds ?? 0;

    const stuck = Boolean(progress?.likely_stuck) || currentStep?.status === 'stuck';
    const hint = failedStep?.error
        ? failedStep.error
        : stuck
          ? progress?.note ||
            'This step is taking longer than expected. Check the API worker or start a new generate.'
          : 'You can leave this page — we will email you when the analysis is ready.';

    const toggleLabel = minimized ? 'Expand progress' : 'Minimize';

    if (minimized) {
        return (
            <aside
                className={styles.insightsGeneratingDockMinimized}
                role="status"
                aria-live="polite"
                aria-busy={!failedStep}>
                <button
                    type="button"
                    className={styles.insightsGeneratingDockToggle}
                    onClick={() => setMinimized(false)}
                    aria-expanded="false">
                    {failedStep ? `Failed · ${versionLabel}` : `${versionLabel} · ${currentLabel}`}
                    {!failedStep ? ` · ${formatDuration(elapsedSeconds)}` : ''}
                    <span className={styles.insightsGeneratingDockToggleAction}>{toggleLabel}</span>
                </button>
            </aside>
        );
    }

    return (
        <aside
            className={styles.insightsGeneratingDock}
            role="status"
            aria-live="polite"
            aria-busy={!failedStep}>
            <div className={styles.insightsGeneratingCard}>
                <div className={styles.insightsGeneratingDockHeader}>
                    <p className={styles.insightsGeneratingDockTitle}>Generating insights</p>
                    <button
                        type="button"
                        className={styles.insightsGeneratingDockToggleBtn}
                        onClick={() => setMinimized(true)}
                        aria-expanded="true">
                        Minimize
                    </button>
                </div>
                {!failedStep ? (
                    <div className={styles.insightsGeneratingDots} aria-hidden="true">
                        <span className={styles.insightsGeneratingDot} />
                        <span className={styles.insightsGeneratingDot} />
                        <span className={styles.insightsGeneratingDot} />
                    </div>
                ) : (
                    <div className={styles.insightsGeneratingFailMark} aria-hidden="true">
                        ✕
                    </div>
                )}
                <p className={styles.insightsGeneratingLabel}>
                    {failedStep ? `Failed at ${failedStep.label}` : currentLabel}
                </p>
                <p className={styles.insightsGeneratingMeta}>
                    {versionLabel}
                    {!failedStep ? ` · running ${formatDuration(elapsedSeconds)}` : ''}
                    {!failedStep && currentStep
                        ? ` · this step ${formatDuration(currentElapsed)}`
                        : ''}
                </p>
                {hint ? (
                    <p
                        className={
                            failedStep || stuck
                                ? styles.insightsGeneratingHintStuck
                                : styles.insightsGeneratingHint
                        }>
                        {hint}
                    </p>
                ) : null}
                {steps.length ? (
                    <ol className={styles.insightsGeneratingStages}>
                        {steps.map(step => {
                            const timing =
                                step.status === 'complete' && step.duration_seconds != null
                                    ? formatDuration(step.duration_seconds)
                                    : step.status === 'running' || step.status === 'stuck'
                                      ? formatDuration(step.elapsed_seconds)
                                      : null;
                            return (
                                <li
                                    key={step.key}
                                    className={`${styles.insightsGeneratingStage} ${stepStatusClass(
                                        step.status,
                                        step.is_current,
                                    )}`}>
                                    <span
                                        className={
                                            step.status === 'running'
                                                ? styles.insightsGeneratingStageSpinner
                                                : ''
                                        }
                                        aria-hidden="true">
                                        {stepIcon(step.status)}
                                    </span>
                                    <span className={styles.insightsGeneratingStageLabel}>
                                        {step.label}
                                    </span>
                                    {timing ? (
                                        <span className={styles.insightsGeneratingStageTime}>
                                            {timing}
                                        </span>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ol>
                ) : null}
            </div>
        </aside>
    );
};

export default InsightsGeneratingOverlay;
